<template>
    <app-layout title="Loads">

        <page-header>
            <template #title>Loads</template>

            <template #actions v-if="!isOnlyPicker">
                <div class="flex flex-col sm:flex-row justify-between gap-x-4 gap-y-2">
                    <!-- Print button / dropdown -->
                    <!-- Retail -->
                    <jet-dropdown align="right">
                        <template #trigger>
                            <jet-tertiary-button size="small" :disabled="selectedCount == 0">
                                <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                                <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                            </jet-tertiary-button>
                        </template>
                        <template #content>
                            <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('summary')">Load Summary</div>
                        </template>
                    </jet-dropdown>
                </div>

                <jet-tertiary-button type="button" :disabled="form.processing" @click="showPdfModal = true">
                    <i class="fa-solid fa-file-pdf"></i>&nbsp;View PDFs
                </jet-tertiary-button>
                <jet-button
                    v-if="syncOptimoRoute || loadAuthority === 'optimoroute'"
                    type="button"
                    :disabled="form.processing || isSyncing"
                    @click="syncOptimoRouteNow"
                >
                    {{ isSyncing ? 'Syncing…' : 'Sync now' }}
                </jet-button>
                <jet-button
                    v-if="loadAuthority === 'routific'"
                    type="button"
                    :disabled="form.processing"
                    @click="confirmingAutoGenLoads = true"
                >
                    Auto Generate Loads
                </jet-button>
                <jet-button
                    v-if="loadAuthority === 'optimoroute'"
                    type="button"
                    :disabled="form.processing"
                    @click="confirmingAutoGenOptimoRouteLoads = true"
                >
                    Auto Generate Loads
                </jet-button>
                <jet-button type="button" :href="route('loads.create')" :disabled="form.processing" >
                    Add
                </jet-button>
            </template>
        </page-header>

        <div class="px-8">
            <div class="flex">
                <form class="flex flex-1 gap-x-2 flex-wrap" @submit.prevent="handleSubmit">
                    <!-- Search / Filters -->
                    <div class="flex flex-1 gap-x-2">
                        <div class="flex flex-col flex-1">
                            <jet-label for="date_range" value="Date Range" />
                            <datepicker inputClassName="dp-custom-input"
                                @update:modelValue="handleSubmit"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart="0"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="load_type" value="Load Type" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.load_type"
                                :options="loadTypeOptions"
                                placeholder="All Load Types"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="form.load_type == 'pickup'" class="flex flex-col flex-1">
                            <jet-label for="Location" value="Location" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.location_id"
                                :options="locationOptions"
                                placeholder="All Locations"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="form.load_type == 'delivery'" class="flex flex-col flex-1">
                            <jet-label for="route_type" value="Route Type" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.route_type"
                                :options="routeTypeOptions"
                                placeholder="All Route Types"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1 min-w-36">
                            <jet-label for="status" value="Status" />
                            <multiselect class="multi-sm min-w-36 h-7 text-base"
                                v-model="form.status"
                                :options="statusOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="search" value="Search" />
                            <jet-input type="text" class="h-7 text-base" name="search" v-model="form.search" @input="handleSubmit" placeholder="Search..."/>
                        </div>
                    </div>
                </form>

                <div class="self-end">
                    <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>
            </div>
        </div>

        <div class="px-8 pt-2 pb-4">
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <th class="hidden md:table-cell text-center w-0">
                                <jet-input type="checkbox" :checked="printAll === true" @change="selectAll" class="h-5 w-5"></jet-input>
                            </th>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="date" :direction="sort_direction" :column="sort_column">Date</sortable-header>
                            <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" sort-by="type" :direction="sort_direction" :column="sort_column">Load Type</sortable-header>
                            <sortable-header v-if="form.load_type != 'pickup'" class="text-left whitespace-nowrap" @sort="handleSort" sort-by="route_type" :direction="sort_direction" :column="sort_column">Route Type</sortable-header>
                            <sortable-header v-if="form.load_type != 'pickup'" class="text-left" @sort="handleSort" sort-by="route" :direction="sort_direction" :column="sort_column">Route</sortable-header>
                            <sortable-header v-if="form.load_type != 'pickup'" class="text-left" @sort="handleSort" sort-by="truck" :direction="sort_direction" :column="sort_column">Truck</sortable-header>
                            <sortable-header v-if="form.load_type != 'pickup'" class="text-left" @sort="handleSort" sort-by="driver" :direction="sort_direction" :column="sort_column">Driver</sortable-header>
                            <sortable-header v-if="form.load_type != 'delivery'" class="text-left whitespace-nowrap" @sort="handleSort" sort-by="location_id" :direction="sort_direction" :column="sort_column">Location</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="departure_time" :direction="sort_direction" :column="sort_column">Time</sortable-header>
                            <th class="text-left">Status</th>
                            <sortable-header class="text-right" @sort="handleSort" sort-by="actual_load_orders_count" :direction="sort_direction" :column="sort_column">Orders</sortable-header>
                            <th class="text-right">Items</th>
                            <th class="text-right">Picked</th>
                            <th class="text-right">Loaded</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="load, index in loads.data"
                            :key="load.id"
                            :style="{
                                'background': getBackground(load, index)
                            }">
                            <td class="hidden md:table-cell text-center">
                                <input type="checkbox" v-model="loadsToPrint" :value="load.id" class="h-5 w-5 rounded" />
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ formatDate(load.date) }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ (load.type != null) ? load.type_def : '-' }}
                                </Link>
                            </td>
                            <td v-if="form.load_type != 'pickup'" class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ (load.route_type_def != null) ? load.route_type_def : '-' }}
                                </Link>
                            </td>
                            <td v-if="form.load_type != 'pickup'" class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ (load.route != null) ? load.route : '-' }}
                                </Link>
                            </td>
                            <td v-if="form.load_type != 'pickup'" class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ (load.truck != null) ? load.truck : '-' }}
                                </Link>
                            </td>
                            <td v-if="form.load_type != 'pickup'" class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ (load.driver != null) ? load.driver : '-' }}
                                </Link>
                            </td>
                            <td v-if="form.load_type != 'delivery'" class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ load.type == 'pickup' ? load.location_name : '-' }}
                                </Link>
                            </td>
                            <td v-if="load.type == 'delivery'" class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ (load.departure_time != null) ? formatTime(load.departure_time) : '-' }}
                                </Link>
                            </td>
                            <td v-else class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ load.shipping_time_label }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ load.status }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ load.picked_load_orders_count }} / {{ load.actual_load_orders_count }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ load.picked_load_order_items_count }} / {{ load.load_order_items_count }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ load.picking_status_prc }}%
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('loads.show', load.id)">
                                    {{ load.loading_status_prc }}%
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-show="selectedCount > 0" class="mt-1 text-gray-400 text-sm">
                Selected: {{ selectedCount }} load{{ selectedCount == 1 ? '' : 's'}}
            </div>

            <div class="flex justify-between">
                <pagination class="inline-flex" :links="loads.links" />
                
                <div class="mt-6 text-sm">
                    <div class="flex gap-x-2"><div class="w-3 h-3 mt-1 border-gray-500 rounded-full bg-blue-500 bg-opacity-20"></div> Picked</div>
                    <div class="flex gap-x-2"><div class="w-3 h-3 mt-1 border-gray-500 rounded-full bg-green bg-opacity-20"></div> Loaded</div>
                </div>
            </div>

            <div v-if="pickingData || loadingPickingData" class="w-full mt-6">
                <div class="text-lg font-bold flex items-center gap-2">
                    Picking Statistics
                    <span v-if="loadingPickingData" class="text-sm text-gray-400 font-normal pt-1">Loading…</span>
                </div>

                <picking-data v-if="pickingData" :pickingData="pickingData" />
            </div>
        </div>

        <jet-confirmation-modal :show="confirmingAutoGenLoads" @close="confirmingAutoGenLoads = false">
        <template #title>
            Autogenerate Loads from Routific
        </template>

        <template #content>
            <div class="mb-4">
                Are you sure you want to autogenerate today's finalized loads from Routific? Once loads are generated, any changes made in Routific must be made manually within the loads to match.
            </div>
            <div class="mb-4">
                Running this autogeneration in the morning (12:00am to 11:59am) will only generate loads for today's finalized non-evening routes. Running this autogeneration in the afternoon (12:00pm to 11:59pm) will only generate loads for today's finalized evening routes.
            </div>
            <div class="text-sm text-gray-600">
                OptimoRoute locked routes are polled every 5 minutes and via "Sync now". Automatic day (00:10) and evening (12:10) syncs handle PDF generation when OptimoRoute holds system-wide load authority.
            </div>
        </template>

        <template #footer>
            <jet-button class="mr-2" :disabled="isGenerating" @click="autoGenerateLoads">
                {{ isGenerating ? 'Generating…' : 'Autogenerate Loads' }}
            </jet-button>

            <jet-tertiary-button @click="confirmingAutoGenLoads = false">
                Cancel
            </jet-tertiary-button>
        </template>
    </jet-confirmation-modal>

        <jet-confirmation-modal :show="confirmingAutoGenOptimoRouteLoads" @close="confirmingAutoGenOptimoRouteLoads = false">
            <template #title>
                Autogenerate Loads from OptimoRoute
            </template>

            <template #content>
                <div class="mb-4">
                    Are you sure you want to autogenerate today's loads from OptimoRoute? This pulls locked routes into warehouse loads and regenerates PDFs for the active shift. Unlocked routes are ignored until they are locked in OptimoRoute.
                </div>
                <div class="mb-4">
                    Running this in the morning (12:00am to 12:00pm) generates day loads. Running this in the afternoon (after 12:00pm) generates evening loads.
                </div>
                <div class="text-sm text-gray-600">
                    Locked routes are also synced every 5 minutes and via "Sync now" (load data only, no PDFs). Scheduled day (00:10) and evening (12:10) syncs still run automatically for PDF generation.
                </div>
            </template>

            <template #footer>
                <jet-button class="mr-2" :disabled="isGeneratingOptimoRoute" @click="autoGenerateOptimoRouteLoads">
                    {{ isGeneratingOptimoRoute ? 'Generating…' : 'Autogenerate Loads' }}
                </jet-button>

                <jet-tertiary-button @click="confirmingAutoGenOptimoRouteLoads = false">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

    <pdf-viewer-modal 
        :show="showPdfModal" 
        @close="showPdfModal = false"
        :from="pdfModalFrom"
        :to="pdfModalTo"
    />

    </app-layout>
</template>

<script>
import { usePage, useForm, Link } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import NProgress from 'nprogress'
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import JetDialogModal from '@/Jetstream/DialogModal.vue'
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import { DateTime } from "luxon";
import Pagination from '@/Jetstream/Pagination';
import Datepicker from '@vuepic/vue-datepicker';
import Multiselect from '@vueform/multiselect';
import JetDropdown from '@/Jetstream/Dropdown';
import SortableHeader from '@/Shared/SortableHeader';
import PickingData from '@/Pages/Loads/Partials/PickingData.vue';
import PdfViewerModal from '@/Pages/Loads/Partials/PdfViewerModal.vue';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    
    setup(props) {
        const { isOnlyPicker } = UseUserAuth(usePage().props.value.user);

        const form = useForm({
            search: props.filters.search,
            from: props.filters.from,
            to: props.filters.to,
            load_type: props.filters.load_type,
            location_id: props.filters.location_id,
            route_type: props.filters.route_type,
            status: props.filters.status,
            sort_column: props.sort_column,
            sort_direction: props.sort_direction,
        });

        return { isOnlyPicker, form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetInput,
        JetLabel,
        JetTertiaryButton,
        JetDialogModal,
        Link,
        Pagination,
        Datepicker,
        Multiselect,
        JetDropdown,
        SortableHeader,
        PickingData,
        JetConfirmationModal,
        PdfViewerModal
    },

    props: [
        'loads',
        'filters',
        'loadTypeOptions',
        'locationOptions',
        'routeTypeOptions',
        'sort_column',
        'sort_direction',
        'other_pages_count',
        'loadAuthority',
        'syncOptimoRoute',
    ],

    data() {
        return {
            interval: null,

            date: null,
            printAll: false,            
            loadsToPrint: [],
            
            statusOptions: [
                { value: 0, label: 'New' },
                { value: 1, label: 'In Progress - Unpicked' },
                { value: 2, label: 'In Progress - Picked' },
                { value: 3, label: 'Invoiced' },
            ],
            confirmingAutoGenLoads:false,
            confirmingAutoGenOptimoRouteLoads: false,
            isGenerating: false,
            isGeneratingOptimoRoute: false,
            isSyncing: false,
            showPdfModal: false,

            // pickingData is fetched async so it doesn't block initial page render
            pickingData: null,
            loadingPickingData: false,
            pickingDataRequestId: 0,
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.printAll = false;
            this.loadsToPrint = [];

            // Clear stale stats immediately so they don't linger over the newly filtered loads
            this.pickingData = null;
            this.loadingPickingData = true;
            // Invalidate any in-flight fetch so its response can't overwrite the fresh one
            this.pickingDataRequestId++;

            this.form
                .transform((data) => ({
                    ...data,
                    from: this.date ? DateTime.fromJSDate(this.date[0]).toISODate() : null,
                    to: this.date ? DateTime.fromJSDate(this.date[1]).toISODate() : null,
                }))
                .get(route('loads.index'), {
                    preserveState: true,
                    // Refresh picking stats after the filter change lands so they match the new result set
                    onSuccess: () => this.getPickingData(),
                });
        }, 250),

        handleSort (sort) {
            this.form.sort_column = sort.column;
            this.form.sort_direction = sort.direction;
        },

        handleReset () {
            this.date = null;
            this.form.from = null;
            this.form.to = null;
            this.form.search = '';
            this.form.load_type = null;
            this.form.location_id = null;
            this.form.route_type = null;
            this.form.status = null;
            this.form.sort_column = 'date';
            this.form.sort_direction = 'desc';

            this.handleSubmit();
        },

        setDateRange() {
            if(this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        formatDate (date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if(start && end)
            {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        },

        formatTime(timestamp) {
            let date = DateTime.fromSQL(timestamp).invalid ? DateTime.fromISO(timestamp) : DateTime.fromSQL(timestamp);

            if (date.c.hour === 0 && date.c.minute === 0 && date.c.second === 0) {
                return '';
            }

            return date.toFormat('h:mm a');
        },

        handlePrint(type = '') {
            window.open((route('loads.multi.print', {
                printAll: this.printAll,
                exclude: this.loadsToExclude.join(','),
                printType: type,
                loads: this.loadsToPrint.join(','),
            } )), "_blank");

            this.reloadPage();
        },

        getBackground (load, index) {
            if (load.picking_status_prc == 0)
                return null;
            else if ((load.picking_status == 0 || load.loading_status == 0) && index % 2 == 0)
                return 'linear-gradient(90deg, rgba(133, 199, 30, 0.2)' + load.loading_status_prc + '%, rgba(59, 130, 246, 0.2)' + (load.loading_status_prc + (load.loading_status_prc == 0 ? 0 : 2)) + '%, rgba(59, 130, 246, 0.2)' + load.picking_status_prc + '%, transparent ' + (load.picking_status_prc + 2) + '%)';
            else if ((load.picking_status == 0 || load.loading_status == 0) && index % 2 == 1)
                return 'linear-gradient(90deg, rgba(133, 199, 30, 0.2)' + load.loading_status_prc + '%, rgba(59, 130, 246, 0.2)' + (load.loading_status_prc + (load.loading_status_prc == 0 ? 0 : 2)) + '%, rgba(59, 130, 246, 0.2)' + load.picking_status_prc + '%, rgba(249, 249, 249, 1) ' + (load.picking_status_prc + 2) + '%)';
            else return 'rgba(133, 199, 30, 0.2)';
        },
        
        reloadPage() {
            this.printAll = false;
            this.loadsToPrint = [];

            this.$inertia.reload({ only: ['loads'] });
        },

        selectAll (e) {
            const isChecked = e.target.checked;
            this.printAll = isChecked;

            this.loadsToPrint = [];

            if (isChecked) {
                this.loads.data.forEach(o => this.loadsToPrint.push(o.id));
            }
        },

        getPickingData () {
            // Guard against stale responses if multiple fetches overlap (e.g. fast filter changes)
            const requestId = ++this.pickingDataRequestId;
            this.loadingPickingData = true;

            const params = this.filters ? {
                from: this.filters.from,
                to: this.filters.to,
                load_type: this.filters.load_type,
                location_id: this.filters.location_id,
                route_type: this.filters.route_type,
                status: this.filters.status,
                search: this.filters.search,
            } : {};

            axios.get(route('loads.picking_data'), { params })
                .then(response => {
                    if (requestId !== this.pickingDataRequestId) return;
                    // Backend returns null/[] when there's no date range or no matching orders
                    this.pickingData = response.data && Object.keys(response.data).length ? response.data : null;
                })
                .catch(error => {
                    if (requestId !== this.pickingDataRequestId) return;
                    console.error('Failed to load picking stats', error);
                })
                .finally(() => {
                    if (requestId !== this.pickingDataRequestId) return;
                    this.loadingPickingData = false;
                });
        },

        autoGenerateLoads(){
            this.isGenerating = true;
            axios.post(route('loads.auto_generate'), {
            }).then(() => {
                this.isGenerating = false;
                this.confirmingAutoGenLoads = false;
            }).catch(error => {
                this.isGenerating = false;
                this.confirmingAutoGenLoads = false;
                console.error(error);
            });
        },

        autoGenerateOptimoRouteLoads(){
            this.isGeneratingOptimoRoute = true;
            axios.post(route('loads.auto_generate_optimoroute'), {
            }).then(() => {
                this.isGeneratingOptimoRoute = false;
                this.confirmingAutoGenOptimoRouteLoads = false;
            }).catch(error => {
                this.isGeneratingOptimoRoute = false;
                this.confirmingAutoGenOptimoRouteLoads = false;
                console.error(error);
            });
        },

        syncOptimoRouteNow(){
            this.isSyncing = true;
            axios.post(route('loads.sync_optimoroute'), {
            }).then(() => {
                this.isSyncing = false;
            }).catch(error => {
                this.isSyncing = false;
                console.error(error);
            });
        }
    },

    mounted() {
        this.setDateRange();
        this.getPickingData();
    },

    created () {
        this.interval = setInterval(() => {
            if (!NProgress.isStarted())
            {
                this.$inertia.reload({ only: ['loads'] });
                this.getPickingData();
            }
        }, 30 * 1000)
    },

    beforeUnmount() {
        clearInterval(this.interval);
    },

    computed: {
        loadsToExclude() {
            return this.loads.data.filter(i => !this.loadsToPrint.includes(i.id)).map(i => {return i.id});
        },

        selectedCount() {
            return this.printAll === true ? this.loadsToPrint.length + this.other_pages_count : this.loadsToPrint.length;
        },

        pdfModalFrom() {
            if (this.date && this.date.length === 2) {
                return DateTime.fromJSDate(this.date[0]).toISODate();
            }
            return null;
        },

        pdfModalTo() {
            if (this.date && this.date.length === 2) {
                return DateTime.fromJSDate(this.date[1]).toISODate();
            }
            return null;
        },
    },

    watch: {
        "form.sort_column": {
            handler: function() {
                this.handleSubmit();
            }
        },

        "form.sort_direction": {
            handler: function() {
                this.handleSubmit();
            }
        },
    }
}
</script>

<style scoped>

</style>

<style>
.dp-custom-input {
    @apply h-7;
}
</style>
